import {
  format,
  formatDistanceToNow,
  isToday,
  isTomorrow,
  isYesterday,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
} from 'date-fns';
import { ACTIVITY_CHAT_GRACE_AFTER_LISTING_MS } from '../../../shared/constants';

export const formatActivityDate = (dateStr) => {
  const date = new Date(dateStr);
  if (isToday(date)) return `Today, ${format(date, 'h:mm a')}`;
  if (isTomorrow(date)) return `Tomorrow, ${format(date, 'h:mm a')}`;
  return format(date, 'EEE, MMM d · h:mm a');
};

export const formatRelativeTime = (dateStr) => {
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
};

/** Compact inbox labels: 2m, 1h, Yesterday, 2d */
export const formatInboxTime = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const mins = differenceInMinutes(now, d);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = differenceInHours(now, d);
  if (hours < 24) return `${Math.max(1, hours)}h`;
  if (isYesterday(d)) return 'Yesterday';
  const days = differenceInDays(now, d);
  if (days < 7) return `${days}d`;
  return format(d, 'MMM d');
};

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => (deg * Math.PI) / 180;

export const formatDistance = (km) => {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
};

export const getInitials = (name) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const getCategoryInfo = (categoryId) => {
  const map = {
    sports: { label: 'Sports', emoji: '🏅', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    fitness: { label: 'Fitness', emoji: '💪', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    learning: { label: 'Learning', emoji: '📚', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
    social: { label: 'Social', emoji: '🎉', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
    outdoors: { label: 'Outdoors', emoji: '🌿', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    creative: { label: 'Creative', emoji: '🎨', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
    gaming: { label: 'Gaming', emoji: '🎮', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
    food: { label: 'Food & Cooking', emoji: '🍳', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    music: { label: 'Music', emoji: '🎵', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
    study: { label: 'Study', emoji: '📝', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
    travel: { label: 'Travel', emoji: '✈️', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  };
  return map[categoryId] || { label: categoryId, emoji: '📌', color: 'bg-surface-100 text-surface-700' };
};

/**
 * Opens Google Maps directions (from the user’s current position) in a new tab.
 * Accepts GeoJSON-style coordinates [lng, lat] and/or a free-text address.
 */
export const openGoogleMapsDirectionsToDestination = (location) => {
  if (!location) return;
  let lat;
  let lng;
  if (location.latitude != null && location.longitude != null) {
    lat = Number(location.latitude);
    lng = Number(location.longitude);
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    const coords = location.coordinates;
    if (Array.isArray(coords) && coords.length === 2) {
      lng = Number(coords[0]);
      lat = Number(coords[1]);
    }
  }
  const address = typeof location.address === 'string' ? location.address.trim() : '';
  const hasLatLng = Number.isFinite(lat) && Number.isFinite(lng);
  const url = hasLatLng
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : address
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
      : null;
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

/** Title-case a single word for UI labels (e.g. "participant" → "Participant"). */
export const capitalizeFirst = (str) => {
  if (str == null || str === '') return '';
  const s = String(str);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/**
 * Singular or plural noun for a count, title-cased for cards (e.g. pluralNoun(3, 'participant') → "Participants").
 * @param {number} count
 * @param {string} singular
 * @param {string} [plural] irregular plural; defaults to singular + "s"
 */
export const pluralNoun = (count, singular, plural) => {
  const n = Number(count);
  if (!Number.isFinite(n)) return capitalizeFirst(plural ?? `${singular}s`);
  const base = Math.abs(n) === 1 ? singular : plural ?? `${singular}s`;
  return capitalizeFirst(base);
};

/** e.g. formatCountNoun(1, 'participant') → "1 Participant", (3, 'activity', 'activities') → "3 Activities" */
export const formatCountNoun = (count, singular, plural) => {
  const n = Number(count);
  const safe = Number.isFinite(n) ? n : 0;
  return `${safe} ${pluralNoun(safe, singular, plural)}`;
};

/** Yaarvi voice: people in a vibe (e.g. "3 yaars in", "1 yaar in"). */
export const formatYaarsInLine = (count) => {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n === 0) return '0 yaars in';
  if (n === 1) return '1 yaar in';
  return `${n} yaars in`;
};

/** e.g. "10 yaars in · 5 spots left" */
export const formatYaarsInAndSpotsLeft = (joined, maxParticipants) => {
  const j = Math.max(0, Math.floor(Number(joined) || 0));
  const max = Math.max(0, Math.floor(Number(maxParticipants) || 0));
  const spotsLeft = Math.max(0, max - j);
  return `${formatYaarsInLine(j)} · ${spotsLeft} spots left`;
};

/** Matches activity detail / Explore “past” — completed, cancelled, or no longer upcoming/live. */
export const isActivityListingEnded = (activity, now = new Date()) => {
  const eventStart = activity.dateTime ? new Date(activity.dateTime) : null;
  const eventExpiry = activity.expiresAt ? new Date(activity.expiresAt) : null;
  const expiryValid = eventExpiry && !Number.isNaN(eventExpiry.getTime());
  const startValid = eventStart && !Number.isNaN(eventStart.getTime());
  const stillInQuickOrLiveWindow = expiryValid && eventExpiry > now;
  const stillScheduled =
    startValid &&
    eventStart >= now &&
    (!activity.expiresAt || !expiryValid);
  const stillActiveAsListing = stillInQuickOrLiveWindow || stillScheduled;
  return (
    activity.status === 'completed' ||
    activity.status === 'cancelled' ||
    !stillActiveAsListing
  );
};

/** True when the group chat composer should be disabled (after listing end + grace, or completed/cancelled). */
export const isActivityChatReadOnly = (activity, now = new Date()) => {
  if (activity.status === 'completed' || activity.status === 'cancelled') return true;

  const eventStart = activity.dateTime ? new Date(activity.dateTime) : null;
  const eventExpiry = activity.expiresAt ? new Date(activity.expiresAt) : null;
  const expiryValid = eventExpiry && !Number.isNaN(eventExpiry.getTime());
  const startValid = eventStart && !Number.isNaN(eventStart.getTime());
  const stillInQuickOrLiveWindow = expiryValid && eventExpiry > now;
  const stillScheduled =
    startValid &&
    eventStart >= now &&
    (!activity.expiresAt || !expiryValid);
  const stillActiveAsListing = stillInQuickOrLiveWindow || stillScheduled;

  if (stillActiveAsListing) return false;

  let listingEndMoment = null;
  if (expiryValid) listingEndMoment = eventExpiry;
  else if (startValid) listingEndMoment = eventStart;

  if (!listingEndMoment) return true;

  return now.getTime() >= listingEndMoment.getTime() + ACTIVITY_CHAT_GRACE_AFTER_LISTING_MS;
};

/** Quick/live: start time passed but expiresAt still in the future. */
export const isQuickLiveOngoing = (activity, now = new Date()) => {
  const dt = activity.dateTime ? new Date(activity.dateTime) : null;
  if (!dt || Number.isNaN(dt.getTime()) || dt > now) return false;
  const exp = activity.expiresAt ? new Date(activity.expiresAt) : null;
  return Boolean(exp && !Number.isNaN(exp.getTime()) && exp > now);
};

/**
 * One number for activity cards: legacy `user.rating.average`, else the mean of
 * player / host / safety averages that are greater than 0 (aligned with server ratingService).
 */
export const combinedUserRatingPreview = (user) => {
  if (!user) return 0;
  const legacy = user.rating?.average;
  if (typeof legacy === 'number' && legacy > 0) return legacy;
  const parts = [];
  if (user.averagePlayerRating > 0) parts.push(user.averagePlayerRating);
  if (user.averageHostRating > 0) parts.push(user.averageHostRating);
  if (user.averageSafetyRating > 0) parts.push(user.averageSafetyRating);
  if (parts.length === 0) return 0;
  return Math.round((parts.reduce((s, x) => s + x, 0) / parts.length) * 10) / 10;
};

/** Mean of player / host / safety values present on one Rating document. */
export const singleReviewAverageScore = (rating) => {
  if (!rating) return 0;
  const parts = [];
  if (rating.playerRating > 0) parts.push(rating.playerRating);
  if (rating.hostRating > 0) parts.push(rating.hostRating);
  if (rating.safetyRating > 0) parts.push(rating.safetyRating);
  if (parts.length === 0) return 0;
  return Math.round((parts.reduce((s, x) => s + x, 0) / parts.length) * 10) / 10;
};

export const formatReviewDate = (dateStr) => {
  if (!dateStr) return '';
  return format(new Date(dateStr), 'MMM d, yyyy');
};
